import * as React from "react";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
    Modal,
    Backdrop,
    Fade,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Quarter } from "../variables";
import axios from "axios";
import { encrypt } from "../utils/cipher";
import { ls } from "../variables";

const ROWS_PER_PAGE = 5;

export default function InfoModal({ open, onClose, Year }) {
    const [modalFY, setModalFY] = React.useState("");
    const [modalQuarter, setModalQuarter] = React.useState("");
    const [tableData, setTableData] = React.useState([]);
    const [applied, setApplied] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [searchQuery, setSearchQuery] = React.useState("");

    const handleApply = async () => {
        if (!modalFY || !modalQuarter) return;

        setLoading(true);
        setApplied(true);
        setPage(0);
        setSearchQuery("");

        try {
            const headers = {
                userType: ls.get("userType"),
                email: ls.get("email"),
                authtoken: ls.get("authToken"),
                refreshtoken: ls.get("refreshToken"),
                financial_year: modalFY,
                quarter: modalQuarter,
            };

            const encryptedPayload = await encrypt(headers);

            const res = await axios.post(
                "/api/pms/pms-info",
                {},
                { headers: { headers: encryptedPayload } }
            );

            if (res.data.success) {
                setTableData(res.data.finalResult || []);
            } else {
                setTableData([]);
            }
        } catch (err) {
            console.error("pms-info fetch error:", err);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setModalFY("");
        setModalQuarter("");
        setTableData([]);
        setApplied(false);
        setPage(0);
        setLoading(false);
        setSearchQuery("");
        onClose();
    };

    const filteredData = tableData.filter((row) =>
        row.code?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
    const startRow = filteredData.length === 0 ? 0 : page * ROWS_PER_PAGE + 1;
    const endRow = Math.min((page + 1) * ROWS_PER_PAGE, filteredData.length);
    const paginatedData = filteredData.slice(
        page * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE + ROWS_PER_PAGE
    );

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 300,
                    sx: {
                        backdropFilter: "blur(6px)",
                        backgroundColor: "rgba(10, 25, 47, 0.50)",
                    },
                },
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "95vw", sm: "700px", md: "760px" },
                        bgcolor: "#ffffff",
                        borderRadius: "16px",
                        boxShadow: "0 24px 64px rgba(0,0,0,0.20), 0 4px 16px rgba(21,64,99,0.14)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        border: "1px solid rgba(21,64,99,0.10)",
                    }}
                >
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            background: "linear-gradient(135deg, #154063 0%, #1e5a87 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: "8px",
                                    bgcolor: "rgba(255,255,255,0.18)",
                                    border: "1px solid rgba(255,255,255,0.30)",
                                    backdropFilter: "blur(6px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                }}
                            >
                                <InfoIcon sx={{ color: "#ffffff", fontSize: "1rem" }} />
                            </Box>
                            <Typography
                                sx={{
                                    color: "#ffffff",
                                    fontWeight: 700,
                                    fontSize: "0.98rem",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                PMS Information
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: "rgba(255,255,255,0.85)",
                                borderRadius: "8px",
                                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            px: 3,
                            pt: 2,
                            pb: 2,
                            bgcolor: "#f8fafc",
                            borderBottom: "1px solid #e8eef4",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 1.5,
                                flexWrap: "nowrap",
                            }}
                        >
                            <Box sx={{ flex: "0 0 140px" }}>
                                <FormControl variant="standard" sx={{ m: 0, width: "100%" }}>
                                    <span
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            color: "#4a5568",
                                            marginBottom: 2,
                                            letterSpacing: "0.03em",
                                            display: "block",
                                        }}
                                    >
                                        Financial Year
                                    </span>
                                    <Select
                                        value={modalFY}
                                        onChange={(e) => {
                                            setModalFY(e.target.value);
                                            setTableData([]);
                                            setApplied(false);
                                            setPage(0);
                                            setSearchQuery("");
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    color: "#000000",
                                                    "& .MuiMenuItem-root": { padding: 1, fontSize: "0.82rem" },
                                                },
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={modalFY !== "" ? undefined : () => "Select FY"}
                                        sx={{ fontSize: "0.82rem" }}
                                    >
                                        {Year.map((name) => (
                                            <MenuItem key={name.label} value={name.value}>
                                                {name.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ flex: "0 0 140px" }}>
                                <FormControl variant="standard" sx={{ m: 0, width: "100%" }}>
                                    <span
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            color: "#4a5568",
                                            marginBottom: 2,
                                            letterSpacing: "0.03em",
                                            display: "block",
                                        }}
                                    >
                                        Quarter
                                    </span>
                                    <Select
                                        value={modalQuarter}
                                        onChange={(e) => {
                                            setModalQuarter(e.target.value);
                                            setTableData([]);
                                            setApplied(false);
                                            setPage(0);
                                            setSearchQuery("");
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    color: "#000000",
                                                    "& .MuiMenuItem-root": { padding: 1, fontSize: "0.82rem" },
                                                },
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={
                                            modalQuarter !== "" ? undefined : () => "Select Quarter"
                                        }
                                        sx={{ fontSize: "0.82rem" }}
                                    >
                                        {Quarter.map((name) => (
                                            <MenuItem key={name.label} value={name.value}>
                                                {name.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Apply Button */}
                            <Box sx={{ pb: 0.1, flexShrink: 0 }}>
                                <button
                                    onClick={handleApply}
                                    disabled={loading || !modalFY || !modalQuarter}
                                    style={{
                                        color: "#ffffff",
                                        fontSize: "0.78rem",
                                        backgroundColor: loading || !modalFY || !modalQuarter ? "#a0aec0" : "#154063",
                                        border: loading || !modalFY || !modalQuarter ? "#a0aec0" : "0.125rem solid #154063",

                                        borderRadius: "0.125rem",
                                        minWidth: "6rem",
                                        cursor: loading || !modalFY || !modalQuarter ? "not-allowed" : "pointer",
                                        height: "1.75rem",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "6px",
                                        transition: "background-color 0.2s",
                                        whiteSpace: "nowrap",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!loading && modalFY && modalQuarter)
                                            e.currentTarget.style.backgroundColor = "#1e5a87";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!loading && modalFY && modalQuarter)
                                            e.currentTarget.style.backgroundColor = "#154063";
                                    }}
                                >
                                    {loading ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : null}
                                    APPLY
                                </button>
                            </Box>

                            {/* Search — right-aligned */}
                            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                                <div>
                                    <span
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            color: "#4a5568",
                                            letterSpacing: "0.03em",
                                            display: "block",
                                            marginBottom: 2,
                                        }}
                                    >
                                        Search
                                    </span>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            border: "1px solid #154063",
                                            borderRadius: "4px",
                                            padding: "2px 8px",
                                            height: "25px",
                                            backgroundColor: "#fff",
                                            gap: "6px",
                                            marginTop: "4px",
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#154063"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="11" cy="11" r="8" />
                                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="Search By PMS..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setPage(0);
                                            }}
                                            style={{
                                                border: "none",
                                                outline: "none",
                                                fontSize: "0.8rem",
                                                color: "#154063",
                                                width: "200px",
                                                background: "transparent",
                                            }}
                                        />
                                    </div>
                                </div>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ px: 3, pt: 2, pb: 0 }}>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: "1.5px solid #d0dce8",
                                borderRadius: "10px",
                                overflow: "hidden",
                                "& .MuiTable-root": { borderCollapse: "collapse" },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    tableLayout: "fixed",
                                    width: "100%",
                                    borderCollapse: "collapse",
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {[
                                            { label: "PMS", width: "34%" },
                                            { label: "PMS Action", width: "33%" },
                                            { label: "SEBI Status", width: "33%" },
                                        ].map((col, i, arr) => (
                                            <TableCell
                                                key={col.label}
                                                align="center"
                                                sx={{
                                                    bgcolor: "#154063",
                                                    color: "#ffffff",
                                                    fontSize: "0.82rem",
                                                    letterSpacing: "0.05em",
                                                    py: 1.6,
                                                    width: col.width,
                                                    borderBottom: "2px solid #ffffff",
                                                    borderRight:
                                                        i < arr.length - 1
                                                            ? "2px solid #ffffff"
                                                            : "none",
                                                }}
                                            >
                                                {col.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={3}
                                                align="center"
                                                sx={{ py: 6, borderBottom: "none" }}
                                            >
                                                <CircularProgress size={28} sx={{ color: "#154063" }} />
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedData.length > 0 ? (
                                        paginatedData.map((row, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    bgcolor: index % 2 === 0 ? "#ffffff" : "#f4f8fb",
                                                    "&:hover": { bgcolor: "#e8f0f8" },
                                                    transition: "background-color 0.15s",
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontSize: "0.82rem",
                                                        color: "#154063",
                                                        py: 1.4,
                                                        borderBottom: "1px solid #a0aec0 !important",
                                                        borderRight: "1px solid #a0aec0 !important",
                                                    }}
                                                >
                                                    {row.code}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontSize: "0.82rem",
                                                        color: "#154063",
                                                        py: 1.4,
                                                        borderBottom: "1px solid #a0aec0 !important",
                                                        borderRight: "1px solid #a0aec0 !important",
                                                    }}
                                                >
                                                    {row.pmsAction}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontSize: "0.82rem",
                                                        color: "#154063",
                                                        py: 1.4,
                                                        borderBottom: "1px solid #a0aec0 !important",
                                                        borderRight: "none",
                                                    }}
                                                >
                                                    {row.uploadStatus}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={3}
                                                align="center"
                                                sx={{
                                                    py: 6,
                                                    color: "#9aa5b4",
                                                    fontSize: "0.875rem",
                                                    fontStyle: "italic",
                                                    borderBottom: "none",
                                                }}
                                            >
                                                {applied
                                                    ? "No data found."
                                                    : "Select Financial Year, Quarter and click APPLY to load data."}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box
                        sx={{
                            px: 3,
                            py: 1.4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1,
                            borderTop: "1px solid #e8eef4",
                            mt: 2,
                            bgcolor: "#ffffff",
                        }}
                    >
                        <Typography
                            sx={{ fontSize: "0.8rem", color: "#4a5568", mr: 0.5, userSelect: "none" }}
                        >
                            {filteredData.length === 0
                                ? "0–0 of 0"
                                : `${startRow}–${endRow} of ${filteredData.length}`}
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            sx={{
                                width: 26, height: 26,
                                border: "1px solid",
                                borderColor: page === 0 ? "#e0e8f0" : "#c0ccd8",
                                borderRadius: "6px",
                                color: page === 0 ? "#c8d4e0" : "#154063",
                                bgcolor: "#ffffff",
                                "&:hover": {
                                    bgcolor: page === 0 ? "#ffffff" : "#e8f0f8",
                                    borderColor: page === 0 ? "#e0e8f0" : "#154063",
                                },
                                transition: "all 0.15s",
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>

                        <IconButton
                            size="small"
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1 || filteredData.length === 0}
                            sx={{
                                width: 26, height: 26,
                                border: "1px solid",
                                borderColor:
                                    page >= totalPages - 1 || filteredData.length === 0
                                        ? "#e0e8f0"
                                        : "#c0ccd8",
                                borderRadius: "6px",
                                color:
                                    page >= totalPages - 1 || filteredData.length === 0
                                        ? "#c8d4e0"
                                        : "#154063",
                                bgcolor: "#ffffff",
                                "&:hover": {
                                    bgcolor:
                                        page >= totalPages - 1 || filteredData.length === 0
                                            ? "#ffffff"
                                            : "#e8f0f8",
                                    borderColor:
                                        page >= totalPages - 1 || filteredData.length === 0
                                            ? "#e0e8f0"
                                            : "#154063",
                                },
                                transition: "all 0.15s",
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}